import {useMemo} from 'react'
import {Lang} from 'yougile-lang'
import {ArrUtil, FormErrors, ObjUtil} from 'yougile-util'
import {
  CompaniesBoards,
  DataObjectId,
  GoogleCalendarIntegrationsStatus,
  GoogleCalendarSettings,
  GoogleCalendarTasksType
} from 'yougile-model'
import {Sync} from '@client/sync'
import {
  resetGoogleCalendarSync,
  setGoogleCalendarSettings
} from '@common/google-calendar'
import {useProfileStore} from '@components/account/profile/profile-store'
import {OptionsItem} from '@components/common/base/dropdown-button-menu/types'
import {TreeListItem} from '@components/common/base/tree-list'
import {countSelectedItemsByLevel} from '@components/common/base/tree-list/utils'
import {getCompanies} from '@components/main-menu/handlers/sync-handlers'
import {MessageBox} from '@components/message-box'
import {Project} from '@extensions/project'

export interface GoogleCalendarFormData {
  email: string | undefined
  calendar?: string | undefined
  boards?: DataObjectId[]
  tasksType?: GoogleCalendarTasksType
}

interface SelectedCompaniesBoards {
  [key: DataObjectId]: DataObjectId[]
}

interface GoogleCalendarSettingsForm {
  formData: GoogleCalendarFormData
  formErrors: FormErrors<GoogleCalendarFormData>
  validate: () => FormErrors<GoogleCalendarFormData> | undefined
  formIsChanged: boolean
  close: (value?: boolean) => void
  googleCalendarStatus?: GoogleCalendarIntegrationsStatus | 'error'
  calendarOptions?: OptionsItem[]
  boardsList?: TreeListItem[]
  boardsCompanyMap: {[key in DataObjectId]: DataObjectId}
  calendarValueTitle: string | undefined
  boardsProjectsTitle: string | undefined
  updateForm: (data: Partial<GoogleCalendarFormData>) => void
  onSelectBoards: (itemsIds: DataObjectId[]) => void
  setIfGCFormIsChanged: () => void
  syncData: () => Promise<void>
  isLoading: boolean
  getSelectedCompaniesBoards: (
    selectedItems: DataObjectId[]
  ) => SelectedCompaniesBoards
  setGoogleSettings: (
    settings: GoogleCalendarSettings,
    formIsChanged: boolean
  ) => Promise<boolean>
  resetGoogleSettings: () => Promise<void>
}

export const useGoogleCalendarSettingsForm = (): GoogleCalendarSettingsForm => {
  const [state, store] = useProfileStore()

  const {calendarOptions} = state?.googleCalendarIntegrationData || {}

  const {data: formData, errors: formErrors} = state.googleCalendarForm
  const formIsChanged = state.googleCalendarFormChanged

  const close = store.closeGoogleSettingsPopup
  const updateForm = store.updateGoogleCalendarSettingsFormField

  const {
    boardsList,
    boardsCompanyMap
  }: {
    boardsList: TreeListItem[]
    boardsCompanyMap: {[key in DataObjectId]: DataObjectId}
  } = useBoards()

  const getSelectedCompaniesBoards = (
    selectedItems: DataObjectId[]
  ): SelectedCompaniesBoards => {
    const companyBoards = new Map<DataObjectId, DataObjectId[]>()

    for (const boardId of selectedItems) {
      const companyId = boardsCompanyMap[boardId]
      const companyIdNode = companyBoards.get(companyId)
      companyBoards.set(companyId, [...(companyIdNode || []), boardId])
    }

    return Object.fromEntries(companyBoards)
  }

  const onSelectBoards = (selectedItems: DataObjectId[]): void => {
    updateForm({boards: selectedItems})
  }

  const boardsProjectsTitle = useMemo(() => {
    if (!formData.boards?.length || !boardsList.length) {
      return undefined
    }

    const counts = countSelectedItemsByLevel(formData.boards || [], boardsList)

    if (Object.keys(counts).length === 3) {
      return `${Lang.msg.selectedCompanies(
        counts[0]
      )} / ${Lang.msg.selectedProjects(counts[1])} / ${Lang.msg.selectedBoards(
        counts[2]
      )}`
    } else {
      return `${Lang.msg.selectedProjects(
        counts[0]
      )} / ${Lang.msg.selectedBoards(counts[1])}`
    }
  }, [formData.boards, boardsList])

  const setGoogleSettings = async (
    settings: GoogleCalendarSettings,
    formIsChanged: boolean
  ): Promise<boolean> => {
    try {
      await setGoogleCalendarSettings(settings, formIsChanged)
      await store.syncGoogleCalendarSettingsData()

      return true
    } catch (error) {
      MessageBox.show(Lang.msg.googleCalendar.errorTryAgain, 'warning')

      return false
    }
  }

  const resetGoogleSettings = async (): Promise<void> => {
    try {
      await resetGoogleCalendarSync()
      await store.syncGoogleCalendarSettingsData()
    } catch (error) {
      MessageBox.show(Lang.msg.googleCalendar.errorTryAgain, 'warning')
    }
  }

  return {
    formData,
    formErrors,
    formIsChanged,
    validate: store.validateGCSettingsForm,
    close,
    boardsList,
    boardsCompanyMap,
    googleCalendarStatus: state.googleCalendarSettingsStatus,
    calendarValueTitle:
      (formData.calendar &&
        calendarOptions?.length &&
        calendarOptions.find(calendar => calendar.id === formData.calendar)
          ?.name) ||
      undefined,
    boardsProjectsTitle,
    updateForm,
    onSelectBoards,
    calendarOptions: calendarOptions?.map(calendar => ({
      key: calendar.id,
      text: calendar.name
    })),
    setIfGCFormIsChanged: store.setIfGCFormIsChanged,
    syncData: store.syncGoogleCalendarSettingsData,
    isLoading: state.waitGoogleCalendar,
    getSelectedCompaniesBoards,
    setGoogleSettings,
    resetGoogleSettings
  }
}

const useBoards = (): {
  boardsList: TreeListItem[]
  boardsCompanyMap: {[key in DataObjectId]: DataObjectId}
} => {
  const box = Sync.getCurrentSandbox()
  const companies = getCompanies()

  return useMemo(() => {
    const boardsCompanyMap: {[key in DataObjectId]: DataObjectId} = {}

    const getProjects = (companyId: DataObjectId): TreeListItem[] => {
      return ArrUtil.defined(
        Project.listProjects(companyId, 'not-archive', true)
          .sort(Project.projectOrderComparer)
          .map(project => {
            const boardsOrder = Project.getBoardsOrder(project)
            const boards = boardsOrder.getList()

            if (!boards.length) {
              return undefined
            }

            boards.forEach(board => {
              boardsCompanyMap[board.id] = board.companyId
            })

            return {
              id: project.id,
              title: project.title,
              subItems: boards.map(board => {
                return {
                  id: board.id,
                  title: board.title
                }
              })
            }
          })
      )
    }

    const boards =
      companies.length === 1
        ? getProjects(box.companyId)
        : ArrUtil.defined(
            companies.map(company => {
              const projects = getProjects(company.id)

              if (!projects.length) {
                return undefined
              }

              return {
                id: company.id,
                title: company.name,
                subItems: projects
              }
            })
          )

    return {
      boardsList: boards,
      boardsCompanyMap
    }
  }, [box.companyId])
}

export const getSelectedBoards = (
  companiesBoards?: CompaniesBoards
): DataObjectId[] => {
  if (!companiesBoards) {
    return []
  }
  const result: DataObjectId[] = []
  for (const company of ObjUtil.keys(companiesBoards)) {
    result.push(...companiesBoards[company])
  }
  return result
}

export const getBoardsByCompanies = (
  boards: DataObjectId[],
  boardsCompanyMap: {[key in DataObjectId]: DataObjectId}
): CompaniesBoards => {
  const result: CompaniesBoards = {}

  for (const board of boards) {
    const companyId = boardsCompanyMap[board]
    if (!result[companyId]) {
      result[companyId] = []
    }
    result[companyId].push(board)
  }

  return result
}
